import { useLingui } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { TabMenu } from 'ud-ui/components/tab-menu';

import { ChampionshipStageTab } from 'modules/championship/domain/enums/championship-stage-tab';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { isStageTabAvailable } from 'modules/championship/domain/utils/stage-tab-availability';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { getStageTabLabel, STAGE_MATCH_TAB_INDEX, STAGE_TAB_ORDER } from './constants';

/** На странице матча контент занимает всю высоту — это единственное, что от неё нужно снаружи. */
export const useIsStageMatchRoute = () => Boolean(useMatch(`${Pages.CHAMPIONSHIPS.STEPS.MATCH}/*`));

interface StageTabsProps {
  championshipId: number;
  stage: StageV2;
}

const StageTabs = ({ championshipId, stage }: StageTabsProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();

  // Порядок важен: шаблон `MATCHES/*` подходит и под страницу матча,
  // поэтому более длинный `MATCH` проверяется первым.
  const matchRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.MATCH}/*`);
  const matchesRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.MATCHES}/*`);
  const gridRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.GRID}/*`);
  const teamsRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.TEAMS}/*`);

  const activeGameId = matchRoute ? Number(matchRoute.params.gameId) : null;

  const activeTab = useMemo(() => {
    if (matchRoute) return STAGE_MATCH_TAB_INDEX;
    if (matchesRoute) return STAGE_TAB_ORDER.indexOf(ChampionshipStageTab.MATCHES);
    if (gridRoute) return STAGE_TAB_ORDER.indexOf(ChampionshipStageTab.GRID);
    if (teamsRoute) return STAGE_TAB_ORDER.indexOf(ChampionshipStageTab.TEAMS);
    return 0;
  }, [matchRoute, matchesRoute, gridRoute, teamsRoute]);

  const tabs = useMemo(() => {
    const baseTabs = STAGE_TAB_ORDER.map((tab) => ({
      name: t(getStageTabLabel(tab)),
      // «Редактирование» доступно всегда — этап уже существует, раз мы на его адресе.
      active: tab ? isStageTabAvailable(stage, tab) : true,
    }));

    const game = activeGameId ? stage.matches.find((match) => match.id === activeGameId) : undefined;
    if (!game) {
      return baseTabs;
    }

    const [firstTeam, secondTeam] = game.teams || [];
    const unknownTeam = t`Неизвестно`;
    const first = firstTeam ? firstTeam.name : unknownTeam;
    const second = secondTeam ? secondTeam.name : unknownTeam;
    const name = t`Матч: ${first} - ${second}`;

    return [...baseTabs, { name, active: true }];
  }, [t, stage, activeGameId]);

  const handleChangeActiveTab = useCallback(
    (index: number) => {
      navigate(Pages.CHAMPIONSHIPS.STEPS.builders.stage(championshipId, stage.id, STAGE_TAB_ORDER[index]));
    },
    [navigate, championshipId, stage.id],
  );

  return <TabMenu tabs={tabs} activeTab={activeTab} onChange={handleChangeActiveTab} />;
};

export default StageTabs;
